import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TableSessionContext from './TableSessionContext';
import {
  getTableSession,
  claimTableSessionIdentity,
  recoverTableSession,
  resumeMyTableSession,
  joinTableSession,
  removeTableParticipant,
  requestTableSettlement,
  submitTableRound,
  syncTableDraft,
} from '../../services/tableSession';

const storageKey = (restaurantId, tableRef) => `supu_table_session:${restaurantId}:${tableRef}`;

const readStoredAccess = (restaurantId, tableRef) => {
  try {
    return JSON.parse(localStorage.getItem(storageKey(restaurantId, tableRef)) || 'null');
  } catch {
    return null;
  }
};

const TableSessionState = ({ children }) => {
  const [session, setSession] = useState(null);
  const [participant, setParticipant] = useState(null);
  const [participantToken, setParticipantToken] = useState(null);
  const [joinUrl, setJoinUrl] = useState('');
  const [recoveryUrl, setRecoveryUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const accessRef = useRef(null);
  const activeContextRef = useRef('');
  const previousOwnDraftCount = useRef(0);

  const clearActiveSession = useCallback(() => {
    accessRef.current = null;
    previousOwnDraftCount.current = 0;
    setSession(null);
    setParticipant(null);
    setParticipantToken(null);
    setJoinUrl('');
    setRecoveryUrl('');
    setError('');
  }, []);

  const applyPayload = useCallback((response, accessOverride = null) => {
    const data = response?.data || {};
    const nextSession = data.session || null;
    const nextParticipant = data.participant || null;
    const token = data.participantToken || accessOverride?.participantToken || accessRef.current?.participantToken || null;
    const ownDraftCount = (nextSession?.items || []).filter(
      (item) => item.status === 'draft' && item.participant_id === nextParticipant?.id
    ).length;

    if (previousOwnDraftCount.current > 0 && ownDraftCount === 0) {
      window.dispatchEvent(new Event('table-session-own-round-submitted'));
    }
    previousOwnDraftCount.current = ownDraftCount;
    setSession(nextSession);
    setParticipant(nextParticipant);
    setParticipantToken(token);
    if (data.joinUrl) setJoinUrl(data.joinUrl);
    const nextRecoveryUrl = data.recoveryUrl || accessOverride?.recoveryUrl;
    if (nextRecoveryUrl) setRecoveryUrl(nextRecoveryUrl);
    return { session: nextSession, participant: nextParticipant, participantToken: token };
  }, []);

  const claimIdentity = useCallback(async (accessOverride = null) => {
    const access = accessOverride || accessRef.current;
    if (!access?.sessionId || !access?.participantToken || !localStorage.getItem('tokens')) return null;
    try {
      const response = await claimTableSessionIdentity({
        sessionId: access.sessionId,
        participantToken: access.participantToken,
      });
      if (!response?.success) throw new Error(response?.error || 'Could not update the group identity.');
      return applyPayload(response, access);
    } catch (claimError) {
      console.warn('Group order identity was not updated:', claimError?.message || claimError);
      return null;
    }
  }, [applyPayload]);

  const restore = useCallback(async ({ restaurantId, tableRef, sessionId }) => {
    const contextKey = `${restaurantId || ''}:${tableRef || ''}`;
    if (activeContextRef.current !== contextKey) {
      activeContextRef.current = contextKey;
      clearActiveSession();
    }
    if (!restaurantId || !tableRef || tableRef === '0' || tableRef === 'Online') return null;
    const stored = readStoredAccess(restaurantId, tableRef);
    if (!stored?.participantToken || (sessionId && stored.sessionId !== sessionId)) {
      clearActiveSession();
      return null;
    }

    accessRef.current = { ...stored, restaurantId, tableRef };
    setLoading(true);
    try {
      const response = await getTableSession({ sessionId: stored.sessionId, participantToken: stored.participantToken });
      if (!response?.success) throw new Error(response?.error || 'This table session could not be restored.');
      if (activeContextRef.current !== contextKey) return null;
      const restored = applyPayload(response, stored);
      return (await claimIdentity(stored)) || restored;
    } catch {
      if (activeContextRef.current === contextKey) {
        localStorage.removeItem(storageKey(restaurantId, tableRef));
        clearActiveSession();
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [applyPayload, claimIdentity, clearActiveSession]);

  useEffect(() => {
    const handleIdentityChange = () => { claimIdentity(); };
    window.addEventListener('supu:customer-session-changed', handleIdentityChange);
    return () => window.removeEventListener('supu:customer-session-changed', handleIdentityChange);
  }, [claimIdentity]);

  const join = useCallback(async ({ restaurantId, tableRef, sessionId, displayName, customerId, customerPhone, email, settlementTiming }) => {
    const contextKey = `${restaurantId || ''}:${tableRef || ''}`;
    if (activeContextRef.current !== contextKey) {
      activeContextRef.current = contextKey;
      clearActiveSession();
    }
    setLoading(true);
    setError('');
    try {
      const response = await joinTableSession({
        restaurantId,
        tableRef,
        sessionId: sessionId || undefined,
        displayName,
        customerId: customerId || undefined,
        customerPhone: customerPhone || undefined,
        email: email || undefined,
        settlementTiming: settlementTiming || undefined,
        origin: window.location.origin,
      });
      if (!response?.success) throw new Error(response?.error || 'Could not join this table.');
      if (activeContextRef.current !== contextKey) return null;
      const access = {
        sessionId: response.data.session.id,
        participantToken: response.data.participantToken,
        restaurantId,
        tableRef,
        recoveryUrl: response.data.recoveryUrl || '',
      };
      localStorage.setItem(storageKey(restaurantId, tableRef), JSON.stringify(access));
      accessRef.current = access;
      return { ...applyPayload(response, access), emailSent: Boolean(response.data?.emailSent) };
    } catch (joinError) {
      setError(joinError.message || 'Could not join this table.');
      throw joinError;
    } finally {
      setLoading(false);
    }
  }, [applyPayload, clearActiveSession]);

  const restoreFromResponse = useCallback((response) => {
    if (!response?.success || !response?.data?.session?.id || !response?.data?.participantToken) {
      throw new Error(response?.error || 'Could not return to this table.');
    }
    const restaurantId = response.data.session.restaurant_id;
    const tableRef = response.data.session.restaurant_tables?.table_number;
    const access = {
      sessionId: response.data.session.id,
      participantToken: response.data.participantToken,
      restaurantId,
      tableRef,
      recoveryUrl: response.data.recoveryUrl || '',
    };
    localStorage.setItem(storageKey(restaurantId, tableRef), JSON.stringify(access));
    accessRef.current = access;
    activeContextRef.current = `${restaurantId}:${tableRef}`;
    return { ...applyPayload(response, access), restaurantId, tableRef };
  }, [applyPayload]);

  const recover = useCallback(async (recoveryToken) => {
    setLoading(true);
    setError('');
    try {
      const response = await recoverTableSession({ recoveryToken, origin: window.location.origin });
      return restoreFromResponse(response);
    } catch (recoverError) {
      setError(recoverError?.response?.data?.error || recoverError?.message || 'Could not return to this table.');
      throw recoverError;
    } finally {
      setLoading(false);
    }
  }, [restoreFromResponse]);

  const resume = useCallback(async (sessionId) => {
    setLoading(true);
    setError('');
    try {
      const response = await resumeMyTableSession({ sessionId, origin: window.location.origin });
      return restoreFromResponse(response);
    } catch (resumeError) {
      setError(resumeError?.response?.data?.error || resumeError?.message || 'Could not return to this table.');
      throw resumeError;
    } finally {
      setLoading(false);
    }
  }, [restoreFromResponse]);

  const refresh = useCallback(async () => {
    const access = accessRef.current;
    if (!access?.sessionId || !access?.participantToken) return null;
    try {
      const response = await getTableSession({ sessionId: access.sessionId, participantToken: access.participantToken });
      if (!response?.success) throw new Error(response?.error || 'Could not refresh the table.');
      return applyPayload(response, access);
    } catch {
      return null;
    }
  }, [applyPayload]);

  useEffect(() => {
    if (!session?.id || !participantToken || !['joining', 'active', 'settling'].includes(session.status)) return undefined;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [participantToken, refresh, session?.id, session?.status]);

  const syncDraft = useCallback(async (cartItems) => {
    const access = accessRef.current;
    if (!access?.sessionId || !access?.participantToken || !session?.id) return null;
    setSyncing(true);
    try {
      const response = await syncTableDraft({
        sessionId: access.sessionId,
        participantToken: access.participantToken,
        items: (cartItems || []).map((item) => ({
          client_item_key: item.item_public_id,
          menu_item_id: item.id || item.item_id,
          quantity: item.quantity,
          selected_options: item.selectedOptions || [],
          notes: item.specialInstructions || '',
        })),
      });
      if (!response?.success) throw new Error(response?.error || 'Could not update the table selection.');
      return applyPayload(response, access);
    } finally {
      setSyncing(false);
    }
  }, [applyPayload, session?.id]);

  const submit = useCallback(async ({ submitAll = false, marketingOptIn = false }) => {
    const access = accessRef.current;
    const response = await submitTableRound({
      sessionId: access.sessionId,
      participantToken: access.participantToken,
      submitAll,
      marketing_opt_in: marketingOptIn || undefined,
      idempotencyKey: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
    });
    if (!response?.success) throw new Error(response?.error || 'Could not send this round.');
    const applied = applyPayload(response, access);
    return { ...applied, submission: response?.data?.submission || null };
  }, [applyPayload]);

  const requestSettlement = useCallback(async (request = { splitType: 'together' }) => {
    const access = accessRef.current;
    const settlement = typeof request === 'string' ? { splitType: request } : request;
    const response = await requestTableSettlement({
      sessionId: access.sessionId,
      participantToken: access.participantToken,
      ...settlement,
    });
    if (!response?.success) throw new Error(response?.error || 'Could not request the bill.');
    return applyPayload(response, access);
  }, [applyPayload]);

  const removeParticipant = useCallback(async (participantId) => {
    const access = accessRef.current;
    const response = await removeTableParticipant({
      sessionId: access.sessionId,
      participantToken: access.participantToken,
      participantId,
    });
    if (!response?.success) throw new Error(response?.error || 'Could not remove this guest.');
    return applyPayload(response, access);
  }, [applyPayload]);

  const value = useMemo(() => ({
    session,
    participant,
    participantToken,
    joinUrl,
    recoveryUrl,
    loading,
    syncing,
    error,
    restore,
    claimIdentity,
    join,
    recover,
    resume,
    refresh,
    syncDraft,
    submit,
    requestSettlement,
    removeParticipant,
  }), [claimIdentity, error, join, joinUrl, loading, participant, participantToken, recover, recoveryUrl, refresh, removeParticipant, requestSettlement, restore, resume, session, submit, syncDraft, syncing]);

  return <TableSessionContext.Provider value={value}>{children}</TableSessionContext.Provider>;
};

export default TableSessionState;
